import { FormProvider, useForm } from 'react-hook-form';
import FormFieldInput from '../../forms/form-field-input';
import { isValidEmail, isValidRegistration } from '../../checkout/validation';
import FormFieldButton from '../../forms/form-field-button';
import React, { useState } from 'react';
import { Order } from '@Types/cart';
import { useFormat } from '../../../../helpers/hooks/useFormat';
import { useAccount, useCart } from '@Provider/frontastic';
import Spinner from '../../../commercetools-ui/spinner';
import Button from '../../content/button';

export type RegisterFormProps = {
  order: Order;
  setMaintext: React.Dispatch<React.SetStateAction<string>>;
  setSucess: React.Dispatch<React.SetStateAction<boolean>>;
};

const RegisterForm = ({ order, setMaintext, setSucess }: RegisterFormProps) => {
  const { setCustomerToOrder } = useCart();
  const { register } = useAccount();
  const formMethods = useForm({
    mode: 'onChange',
  });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { formatMessage: formatErrorMessage } = useFormat({ name: 'error' });
  const { formatMessage: formatSuccessMessage } = useFormat({ name: 'success' });

  const [registerError, setRegisterError] = useState('');
  const [registerData, setRegisterData] = useState({ email: order?.email ?? '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = (name, value) => {
    setRegisterData({ ...registerData, [name]: value });
  };

  const registerCall = formMethods.handleSubmit(async (registerData) => {
    registerData.email = order.email;

    if (isValidRegistration(registerData)) {
      setLoading(true);
      //try registering the user with given credentials
      try {
        const response = await register({ email: registerData.email, password: registerData.password });
        if (!response.accountId) {
          setRegisterError(
            formatErrorMessage({
              id: 'account.create.fail',
              defaultMessage: "Sorry. We couldn't create your account..",
            }),
          );
        } else {
          try {
            await setCustomerToOrder(order, response.accountId, true);
            setSucess(true);
            setMaintext(
              formatSuccessMessage({
                id: 'account.linkedToOrderWithVerification',
                defaultMessage:
                  'Your order has been successfully linked to your account. You can find the order in your account. A confirmation email has been sent to {email}.',
                values: { email: registerData.email },
              }),
            );
          } catch (error) {
            setRegisterError(error);
          }
        }
      } catch (err) {
        setRegisterError(formatErrorMessage({ id: 'wentWrong', defaultMessage: 'Sorry. Something went wrong..' }));
      }
      //processing ends
      setLoading(false);
    } else {
      setRegisterError(formatErrorMessage({ id: 'password.noMatch', defaultMessage: "Passwords don't match" }));
    }
  });

  return (
    <>
      {loading ? (
        <div className="flex w-full items-stretch justify-center px-12 py-10">
          <Spinner />
        </div>
      ) : (
        <div className="mx-auto mb-4 w-full bg-neutral-50 p-4 lg:w-[95%] lg:p-6">
          <p className="mb-4 mt-2 text-md font-bold text-neutral-950">
            {formatAccountMessage({
              id: 'thankYouRegister',
              defaultMessage: 'Register to link your order to your account',
            })}
          </p>
          <FormProvider {...formMethods}>
            <form className="space-y-7" onSubmit={(e) => e.preventDefault()} noValidate>
              {registerError && <p className="text-base text-danger-700">{registerError}</p>}
              <div>
                <FormFieldInput
                  name="email"
                  value={order.email}
                  inputType="email"
                  inputAutoComplete="email"
                  placeholder={formatAccountMessage({ id: 'email.enter', defaultMessage: 'Enter your email' })}
                  onChange={handleChange}
                  isDisabled={true}
                />
              </div>
              <div>
                <FormFieldInput
                  name="password"
                  value=""
                  inputType="password"
                  inputAutoComplete="new-password"
                  placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
                  inputClassNames="block w-full appearance-none rounded border border-neutral-600 px-4 py-3.5 text-md placeholder:text-neutral-700 focus:border-neutral-950 focus:outline-none focus:ring-neutral-950"
                  onChange={handleChange}
                  validation={{
                    required: true,
                  }}
                />
              </div>
              <div>
                <FormFieldInput
                  name="confirmPassword"
                  value=""
                  inputType="password"
                  inputAutoComplete="new-password"
                  placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
                  inputClassNames="block w-full appearance-none rounded border border-neutral-600 px-4 py-3.5 text-md placeholder:text-neutral-700 focus:border-neutral-950 focus:outline-none focus:ring-neutral-950"
                  onChange={handleChange}
                  validation={{
                    required: true,
                  }}
                />
              </div>
              <Button style="primary" onClick={registerCall}>
                {formatAccountMessage({
                  id: 'registerToOrder',
                  defaultMessage: 'Register & Connect',
                })}
              </Button>
            </form>
          </FormProvider>
        </div>
      )}
    </>
  );
};

export default RegisterForm;
